import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Swipe-to-dismiss support for mobile: matches native iOS sheet UX.
// A hidden grabber bar sits at the very top of every dialog and is only
// visible on narrow viewports (`sm:hidden`). Pointer-down on the bar
// starts a vertical drag that translates the dialog downward, with light
// resistance past 200px. Release past a 100px threshold slides the
// dialog off-screen and clicks the built-in close button so Radix's own
// state (and any consumer `onOpenChange`) unwinds cleanly; otherwise the
// dialog snaps back to its resting position. Desktop is untouched.
const useSwipeToDismiss = () => {
  const contentRef = React.useRef(null)
  const dragRef = React.useRef({ startY: 0, dragging: false, distance: 0, pointerId: null })

  const setContentRef = React.useCallback((node) => {
    contentRef.current = node
  }, [])

  const onDragPointerDown = (e) => {
    if (typeof window !== 'undefined' && window.innerWidth >= 640) return
    dragRef.current = {
      startY: e.clientY,
      dragging: true,
      distance: 0,
      pointerId: e.pointerId,
    }
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) {}
  }

  const onDragPointerMove = (e) => {
    if (!dragRef.current.dragging) return
    const dy = Math.max(0, e.clientY - dragRef.current.startY)
    dragRef.current.distance = dy
    if (!contentRef.current) return
    // Light rubber-banding past 200px so the drag feels physical rather
    // than sliding forever off-screen.
    const dampened = dy > 200 ? 200 + (dy - 200) * 0.4 : dy
    contentRef.current.style.transform =
      `translate(-50%, calc(-50% + ${dampened}px))`
    contentRef.current.style.transition = 'none'
    contentRef.current.style.opacity = String(Math.max(0.4, 1 - dy / 500))
  }

  const clearInlineStyles = () => {
    if (!contentRef.current) return
    contentRef.current.style.transform = ''
    contentRef.current.style.opacity = ''
    // Wait one tick before clearing transition so the snap-back animates.
    setTimeout(() => {
      if (contentRef.current) contentRef.current.style.transition = ''
    }, 240)
  }

  const onDragPointerUp = (e) => {
    if (!dragRef.current.dragging) return
    const distance = dragRef.current.distance
    dragRef.current.dragging = false
    try { e.currentTarget.releasePointerCapture(dragRef.current.pointerId) } catch (_) {}
    if (!contentRef.current) return
    contentRef.current.style.transition = 'transform 220ms ease, opacity 220ms ease'
    if (distance > 100) {
      // Slide the dialog off the bottom then trigger the real close so
      // Radix and any consumer `onOpenChange` handlers run.
      contentRef.current.style.transform = 'translate(-50%, calc(-50% + 100vh))'
      contentRef.current.style.opacity = '0'
      setTimeout(() => {
        const closeBtn = contentRef.current?.querySelector('[data-testid="dialog-close-btn"]')
        closeBtn?.click()
        // Reset once Radix has torn down the portal so the next open
        // starts fresh (portal unmount would strip the node anyway, but
        // this guards against StrictMode double-renders).
        setTimeout(clearInlineStyles, 220)
      }, 180)
    } else {
      clearInlineStyles()
    }
  }

  return { setContentRef, onDragPointerDown, onDragPointerMove, onDragPointerUp }
}

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const { setContentRef, onDragPointerDown, onDragPointerMove, onDragPointerUp } = useSwipeToDismiss()

  // Forward the internal ref plus any consumer-supplied ref onto the
  // same Radix Content node.
  const combinedRef = React.useCallback((node) => {
    setContentRef(node)
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref, setContentRef])

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={combinedRef}
        className={cn(
          // `pb-[max(1.5rem,env(safe-area-inset-bottom))]` keeps the
          // dialog's bottom action bar clear of the iPhone home-indicator
          // gesture zone (~34px) without changing visual padding on
          // devices that don't expose a safe-area inset. Falls back to
          // the original `p-6`-equivalent 24px elsewhere.
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[calc(100dvh-1rem)] overflow-y-auto overflow-x-hidden overscroll-contain [&>*]:min-w-0 [-webkit-overflow-scrolling:touch] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background px-6 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        {/* Mobile-only iOS-style grabber. Hidden on desktop so the
            gesture never fires when there's no touch input. */}
        <div
          className="sm:hidden absolute left-1/2 top-0 -translate-x-1/2 z-20 flex justify-center px-8 py-2 cursor-grab active:cursor-grabbing touch-none select-none"
          onPointerDown={onDragPointerDown}
          onPointerMove={onDragPointerMove}
          onPointerUp={onDragPointerUp}
          onPointerCancel={onDragPointerUp}
          data-testid="dialog-drag-handle"
          aria-hidden
        >
          <div className="w-10 h-1.5 rounded-full bg-slate-300" />
        </div>
        {children}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200 ring-offset-background transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          data-testid="dialog-close-btn"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
